/**
 * useIsPremium — Single client-side source of truth for premium status.
 * Reads profiles.subscription_tier + subscription_expires_at and caches via React Query.
 * Server-side enforcement still lives in check_*_limit RPCs.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface PremiumStatus {
  isPremium: boolean;
  tier: 'free' | 'premium';
  expiresAt: string | null;
  expired: boolean;
}

export function useIsPremium() {
  const { user } = useAuth();

  const query = useQuery<PremiumStatus>({
    queryKey: ['premium-status', user?.id],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('subscription_tier, subscription_expires_at')
        .eq('id', user!.id)
        .maybeSingle();

      if (error || !data) {
        return { isPremium: false, tier: 'free', expiresAt: null, expired: false };
      }

      const tier = (data.subscription_tier === 'premium' ? 'premium' : 'free') as
        | 'free'
        | 'premium';
      const expiresAt = data.subscription_expires_at ?? null;
      const expired = !!expiresAt && new Date(expiresAt).getTime() < Date.now();
      const isPremium = tier === 'premium' && !expired;

      return { isPremium, tier, expiresAt, expired };
    },
  });

  return {
    isPremium: query.data?.isPremium ?? false,
    tier: query.data?.tier ?? 'free',
    expiresAt: query.data?.expiresAt ?? null,
    expired: query.data?.expired ?? false,
    loading: query.isLoading,
  };
}
